import { useCallback, useEffect, useMemo, useState } from 'react';
import { s } from '../listPageStyles';
import { StatusBadge } from '../ui';
import {
  fetchOpenInvoices, postAllocate, postReallocate,
  effectiveOpen, fmtMoney, fmtDate, invoiceStatusMeta, round2, toNum,
} from './allocationShared';

// Token tabanlı stiller (dark tema otomatik). dps-btn KULLANILMAZ: bu modal DetailShell (.dps)
// dışında da açılıyor ve .dps kökü olmadan primary düğme görünmez olurdu.
const muted = 'var(--text-secondary, #6b7280)';
const strong = 'var(--card-text-strong, #0b2046)';
const danger = 'var(--danger, #e2445c)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  summary: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10,
    padding: '10px 12px', border, borderRadius: 8, background: 'var(--bg-subtle, #f9fafb)', marginBottom: 12,
  },
  sumLabel: { color: muted, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' },
  sumValue: { color: strong, fontSize: 15, fontWeight: 800 },
  tableWrap: { border, borderRadius: 8, overflow: 'auto', maxHeight: '42vh' },
  table: { width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 },
  th: {
    position: 'sticky', top: 0, zIndex: 1, textAlign: 'right', padding: '9px 8px',
    background: 'var(--bg-subtle, #f9fafb)', color: muted, fontSize: 11, fontWeight: 800,
    textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: border,
  },
  thLeft: { textAlign: 'left' },
  td: {
    padding: '8px', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))',
    textAlign: 'right', color: 'var(--text-primary, #111827)', whiteSpace: 'nowrap',
  },
  tdLeft: { textAlign: 'left' },
  open: { fontWeight: 800, color: 'var(--accent, #0073ea)' },
  overdue: { color: danger, fontWeight: 700 },
  amountInput: { ...s.input, height: 32, padding: '4px 8px', textAlign: 'right', borderRadius: 6, width: 120 },
  srcChip: {
    fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 5,
    background: 'var(--accent-soft, rgba(0, 115, 234,0.12))', color: 'var(--accent, #0073ea)',
  },
  band: {
    display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start',
    padding: '10px 12px', marginTop: 12, border, borderRadius: 8, background: 'var(--bg-subtle, #f9fafb)',
  },
  bandItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  reasonBox: { marginTop: 12 },
  reasonLabel: { color: muted, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', display: 'block', marginBottom: 4 },
  warn: {
    marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
    background: 'rgba(226,68,92,0.10)', color: danger, border: `1px solid ${danger}`,
  },
};

/**
 * TAHSİLAT → AÇIK FATURA DAĞITIM MODALI (#Faz2, spec §7 + §10).
 *
 * mode='allocate'   → /allocate   (kuyruktan "Faturaya Eşle")
 * mode='reallocate' → /reallocate (yanlış faturayı düzelt — GEREKÇE ZORUNLU, denetim izi)
 *
 * LİSTE İÇERİĞİ — iki kaynağın BİRLEŞİMİ:
 *   1) /customers/{id}/open-invoices  → ONLINE, yalnız OpenAmount > 0 (kapalı fatura HİÇ GELMEZ).
 *   2) bu tahsilatın MEVCUT eşleştirmeleri → faturayı BU tahsilat kapatmışsa OpenAmount = 0'dır ve
 *      (1)'de görünmez; listelenmezse kullanıcı kendi eşleştirmesini geri alamaz/taşıyamazdı.
 *
 * TAVAN (backend InvoiceAllocationService.OpenExcluding ile birebir):
 *      tavan = OpenAmount + (bu tahsilatın o faturaya hâlihazırda bağladığı tutar)
 * Ayrıca Σ dağıtım ≤ tahsilat tutarı; kalan → Dağıtılmamış (avans).
 */
export function AllocationModal({
  open,
  mode = 'allocate',
  transaction,
  existingAllocations = [],
  onClose,
  onSaved,
  onError,
}) {
  const isReallocate = mode === 'reallocate';
  const txAmount = round2(toNum(transaction?.amount));
  const customerId = transaction?.customerId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ totalOpen: 0, overdueOpen: 0, oldestOpenDays: 0 });
  const [amounts, setAmounts] = useState({}); // invoiceId -> string
  const [reason, setReason] = useState('');
  const [q, setQ] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [loadError, setLoadError] = useState('');

  // existingAllocations çağıranlarda satır-içi dizi olarak geliyor (her render YENİ kimlik).
  // Doğrudan bağımlılık yapılırsa load kimliği her render değişir → useEffect sonsuz fetch döngüsü.
  // Bu yüzden DEĞERE göre stabil bir anahtar üretip türetilenleri ona bağlıyoruz.
  const allocKey = useMemo(
    () => (existingAllocations || [])
      .map((a) => `${a.invoiceId}:${round2(a.allocatedAmount)}`)
      .sort()
      .join('|'),
    [existingAllocations],
  );

  // Bu tahsilatın faturalara HÂLİHAZIRDA bağladığı tutarlar → tavan düzeltmesi + ön-doldurma.
  const mine = useMemo(() => {
    const map = {};
    (existingAllocations || []).forEach((a) => {
      if (a.invoiceId) map[a.invoiceId] = round2(toNum(map[a.invoiceId]) + toNum(a.allocatedAmount));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocKey]);

  // Bu tahsilatın kapattığı faturaların snapshot'ı (kapalı olsalar bile listede kalmalılar).
  const minesInvoices = useMemo(
    () => (existingAllocations || [])
      .filter((a) => a.invoiceId && a.invoice)
      .map((a) => ({ ...a.invoice, daysOverdue: 0, source: 'Bu tahsilat' })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allocKey],
  );

  const load = useCallback(async () => {
    if (!open || !customerId) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchOpenInvoices(customerId, { onlyOverdue, q });
      setSummary({
        totalOpen: data.totalOpen, overdueOpen: data.overdueOpen, oldestOpenDays: data.oldestOpenDays,
      });

      // (2) Bu tahsilatın kapattığı ama artık "açık" görünmeyen faturaları listeye geri kat.
      const seen = new Set(data.invoices.map((i) => i.id));
      const fromMine = minesInvoices.filter((i) => !seen.has(i.id));

      setInvoices([...data.invoices, ...fromMine]);
      // Ön-doldurma: mevcut dağıtım (idempotent — kullanıcı yalnız değiştirmek istediğini değiştirir).
      setAmounts(() => {
        const next = {};
        Object.entries(mine).forEach(([invId, amt]) => { if (amt > 0) next[invId] = String(amt); });
        return next;
      });
    } catch (e) {
      setLoadError(e.message || 'Açık faturalar alınamadı');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [open, customerId, onlyOverdue, q, mine, minesInvoices]);

  useEffect(() => { load(); }, [load]);

  // Modal kapanınca formu sıfırla (bir sonraki açılış temiz başlasın).
  useEffect(() => {
    if (!open) { setReason(''); setQ(''); setOnlyOverdue(false); setAmounts({}); setLoadError(''); }
  }, [open]);

  const capOf = useCallback(
    (inv) => effectiveOpen(inv.openAmount, mine[inv.id]),
    [mine],
  );

  const allocated = useMemo(
    () => round2(Object.values(amounts).reduce((sum, v) => sum + toNum(v), 0)),
    [amounts],
  );
  const unallocated = round2(txAmount - allocated);

  // Σ dağıtım ≤ tahsilat tutarı VE satır ≤ tavan — ikisi de burada kısılır (backend de reddeder).
  const setAmount = (inv, raw) => {
    const cap = capOf(inv);
    const others = round2(allocated - toNum(amounts[inv.id]));
    const roomLeft = round2(txAmount - others); // bu satıra kalan tahsilat payı
    const clamped = Math.max(0, Math.min(toNum(raw), cap, roomLeft));
    setAmounts((prev) => {
      const next = { ...prev };
      if (clamped <= 0) delete next[inv.id];
      else next[inv.id] = String(round2(clamped));
      return next;
    });
  };

  // Seç/bırak: seçilince "tam kapatma" varsayımı → min(tavan, kalan tahsilat).
  const toggle = (inv) => {
    if (toNum(amounts[inv.id]) > 0) {
      setAmounts((prev) => { const next = { ...prev }; delete next[inv.id]; return next; });
      return;
    }
    const others = round2(allocated);
    const roomLeft = round2(txAmount - others);
    const fill = Math.max(0, Math.min(capOf(inv), roomLeft));
    if (fill <= 0) return;
    setAmounts((prev) => ({ ...prev, [inv.id]: String(fill) }));
  };

  const fillFifo = () => {
    let remaining = txAmount;
    const next = {};
    [...invoices]
      .sort((a, b) => new Date(a.dueDate || a.issuedAt || 0) - new Date(b.dueDate || b.issuedAt || 0))
      .forEach((inv) => {
        if (remaining <= 0) return;
        const take = Math.min(capOf(inv), remaining);
        if (take <= 0) return;
        next[inv.id] = String(round2(take));
        remaining = round2(remaining - take);
      });
    setAmounts(next);
  };

  const reasonMissing = isReallocate && !reason.trim();
  const canSubmit = !saving && !loading && !reasonMissing && allocated <= txAmount + 0.005;

  const submit = async () => {
    if (!canSubmit) return;
    const allocations = Object.entries(amounts)
      .map(([invoiceId, v]) => ({ invoiceId, amount: round2(v) }))
      .filter((a) => a.amount > 0);

    setSaving(true);
    try {
      const result = isReallocate
        ? await postReallocate(transaction.id, allocations, reason.trim())
        : await postAllocate(transaction.id, allocations);
      onSaved?.(result, {
        allocated,
        unallocated,
        count: allocations.length,
      });
    } catch (e) {
      onError?.(e.message || 'Eşleştirme kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const title = isReallocate ? 'Yeniden Eşleştir (Düzeltme)' : 'Faturaya Eşle';
  const txLabel = [
    transaction?.documentNo || 'Tahsilat',
    fmtMoney(txAmount),
    fmtDate(transaction?.transactionDate),
  ].filter(Boolean).join(' · ');

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 1000 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>
            {title}
            <span style={{ color: muted, fontWeight: 600, fontSize: 13, marginLeft: 10 }}>{txLabel}</span>
          </h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.modalBody}>
          {!customerId ? (
            <div style={st.warn}>
              Bu tahsilatın müşterisi yok — faturayla eşleştirilemez. Önce tahsilata müşteri bağlayın.
            </div>
          ) : (
            <>
              <div style={st.summary}>
                <div>
                  <div style={st.sumLabel}>Müşteri Açık Borcu</div>
                  <div style={st.sumValue}>{fmtMoney(summary.totalOpen)}</div>
                </div>
                <div>
                  <div style={st.sumLabel}>Vadesi Geçmiş</div>
                  <div style={{ ...st.sumValue, color: summary.overdueOpen > 0 ? danger : strong }}>
                    {fmtMoney(summary.overdueOpen)}
                  </div>
                </div>
                <div>
                  <div style={st.sumLabel}>En Eski Açık Fatura</div>
                  <div style={st.sumValue}>{summary.oldestOpenDays} gün</div>
                </div>
                <div>
                  <div style={st.sumLabel}>Açık Fatura</div>
                  <div style={st.sumValue}>{invoices.length}</div>
                </div>
              </div>

              <div style={st.filters}>
                <input
                  style={{ ...s.filterInput, minWidth: 200 }}
                  placeholder="Fatura no ara…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: strong, fontWeight: 600 }}>
                  <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} />
                  Yalnız vadesi geçmiş
                </label>
                <button style={s.btnSecondary} onClick={fillFifo} disabled={loading || invoices.length === 0}>
                  FIFO Dağıt
                </button>
                <button style={s.btnSecondary} onClick={() => setAmounts({})} disabled={loading || allocated === 0}>
                  Temizle
                </button>
              </div>

              {loadError && <div style={st.warn}>{loadError}</div>}

              <div style={st.tableWrap}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={{ ...st.th, width: 38 }} />
                      <th style={{ ...st.th, ...st.thLeft }}>Fatura No</th>
                      <th style={{ ...st.th, ...st.thLeft }}>Tarih</th>
                      <th style={{ ...st.th, ...st.thLeft }}>Vade</th>
                      <th style={st.th}>Fatura Tutarı</th>
                      <th style={st.th}>Açık Kalan</th>
                      <th style={{ ...st.th, ...st.thLeft }}>Kaynak</th>
                      <th style={st.th}>Dağıtım</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={8} style={{ ...st.td, textAlign: 'center', color: muted, padding: 24 }}>Yükleniyor…</td></tr>
                    )}
                    {!loading && invoices.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ ...st.td, textAlign: 'center', color: muted, padding: 24 }}>
                          Açık fatura yok — tahsilat avans (dağıtılmamış) olarak kalır.
                        </td>
                      </tr>
                    )}
                    {!loading && invoices.map((inv) => {
                      const cap = capOf(inv);
                      const val = amounts[inv.id] ?? '';
                      const selected = toNum(val) > 0;
                      const meta = invoiceStatusMeta(inv.invoiceStatus);
                      return (
                        <tr key={inv.id} style={selected ? { background: 'var(--accent-soft, rgba(0, 115, 234,0.06))' } : undefined}>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <input type="checkbox" checked={selected} onChange={() => toggle(inv)} />
                          </td>
                          <td style={{ ...st.td, ...st.tdLeft }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <b>{inv.invoiceNo}</b>
                              <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                            </div>
                          </td>
                          <td style={{ ...st.td, ...st.tdLeft }}>{fmtDate(inv.issuedAt)}</td>
                          <td style={{ ...st.td, ...st.tdLeft }}>
                            {fmtDate(inv.dueDate)}
                            {inv.daysOverdue > 0 && (
                              <div style={st.overdue}>{inv.daysOverdue} gün gecikmiş</div>
                            )}
                          </td>
                          <td style={st.td}>{fmtMoney(inv.netAmount)}</td>
                          <td style={{ ...st.td, ...st.open }}>{fmtMoney(inv.openAmount)}</td>
                          <td style={{ ...st.td, ...st.tdLeft }}>
                            <span style={st.srcChip}>{inv.source}</span>
                          </td>
                          <td style={st.td}>
                            <input
                              inputMode="decimal"
                              style={st.amountInput}
                              value={val}
                              placeholder="0,00"
                              onChange={(e) => setAmount(inv, e.target.value.replace(',', '.'))}
                              title={`En fazla ${fmtMoney(cap)}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Canlı alt bant (spec §4): Tahsilat · Dağıtılan · Dağıtılmamış */}
              <div style={st.band}>
                <div style={st.bandItem}>
                  <span style={st.sumLabel}>Tahsilat</span>
                  <span style={st.sumValue}>{fmtMoney(txAmount)}</span>
                </div>
                <div style={st.bandItem}>
                  <span style={st.sumLabel}>Dağıtılan</span>
                  <span style={{ ...st.sumValue, color: 'var(--success, #16a34a)' }}>{fmtMoney(allocated)}</span>
                </div>
                <div style={st.bandItem}>
                  <span style={st.sumLabel}>Dağıtılmamış (Avans)</span>
                  <span style={{ ...st.sumValue, color: unallocated > 0 ? 'var(--warn, #fdab3d)' : strong }}>
                    {fmtMoney(unallocated)}
                  </span>
                </div>
              </div>

              {unallocated > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: muted }}>
                  Dağıtılmayan tutar müşterinin <b>avansı</b> olarak kalır ve kuyrukta görünmeye devam eder.
                </div>
              )}

              {isReallocate && (
                <div style={st.reasonBox}>
                  <label style={st.reasonLabel}>Gerekçe * (denetim izi olarak saklanır)</label>
                  <textarea
                    style={{ ...s.input, width: '100%', minHeight: 64, resize: 'vertical', borderColor: reasonMissing ? danger : undefined }}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Örn: Mobil çevrimdışı FIFO yanlış faturayı kapatmış; doğru faturaya taşındı."
                  />
                  {reasonMissing && (
                    <div style={{ color: danger, fontSize: 12, fontWeight: 700, marginTop: 4 }}>
                      Yeniden eşleştirme için gerekçe zorunludur.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={saving}>Vazgeç</button>
          <button style={s.btnPrimary} onClick={submit} disabled={!canSubmit || !customerId}>
            {saving ? 'Kaydediliyor…' : isReallocate ? 'Düzeltmeyi Kaydet' : 'Eşleştir'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AllocationModal;
